import { apiClient } from "./apiClient";

const auth = (accessToken) => ({ headers: { Authorization: `Bearer ${accessToken}` } });

// -> { items: [machine], usage: { tenant_id, used, limit, burn_days, replace_cooldown_hours } | null }
// Superadmin: pass tenantId to get one tenant's machines + quota (without it: every tenant, no quota).
export async function listMachines(accessToken, tenantId) {
  const res = await apiClient.get("/security/machines", { ...auth(accessToken), params: tenantId ? { tenant_id: tenantId } : {} });
  return res.data;
}

// multipart: sbom file (CycloneDX/SPDX JSON or a CSV bill of materials) + machine_name + serial_number (+ edge_id, + tenant_id for superadmin) -> 202 machine (unchanged: true when the same file)
export async function uploadMachineSbom(accessToken, { file, machineName, serialNumber, edgeId, tenantId }) {
  const form = new FormData();
  form.append("sbom", file);
  form.append("machine_name", machineName);
  form.append("serial_number", serialNumber);
  if (edgeId) form.append("edge_id", edgeId);
  if (tenantId) form.append("tenant_id", tenantId);
  const res = await apiClient.post("/security/machines", form, auth(accessToken));
  return res.data;
}

// Server-side pages: status open|resolved|all, KEV first -> { total, limit, offset, items }
export async function getMachineFindings(accessToken, machineId, { status = "open", limit = 10, offset = 0 } = {}) {
  const res = await apiClient.get(`/security/machines/${machineId}/findings`, { ...auth(accessToken), params: { status, limit, offset } });
  return res.data;
}

export async function rescanMachine(accessToken, machineId) {
  const res = await apiClient.post(`/security/machines/${machineId}/rescan`, {}, auth(accessToken));
  return res.data;
}

export async function deleteMachine(accessToken, machineId) {
  await apiClient.delete(`/security/machines/${machineId}`, auth(accessToken));
}

// Superadmin: per-tenant quota. -> { tenant_id, used, limit, burn_days, replace_cooldown_hours }
export async function getMachineSettings(accessToken, tenantId) {
  const res = await apiClient.get(`/security/machine-settings/${tenantId}`, auth(accessToken));
  return res.data;
}

export async function putMachineSettings(accessToken, tenantId, { machineLimit, replaceCooldownHours }) {
  const res = await apiClient.put(`/security/machine-settings/${tenantId}`, { machine_limit: machineLimit, replace_cooldown_hours: replaceCooldownHours }, auth(accessToken));
  return res.data;
}

// Components of one machine, resolved to a CPE or not. Server-side pages.
// status all|cpe|mapped|builtin|unresolved -> { total, limit, offset, counts: {cpe, mapped, builtin, unresolved}, items: [{name, version, vendor, type, cpe, resolution}] }
export async function listComponents(accessToken, machineId, { status = "all", limit = 10, offset = 0 } = {}) {
  const res = await apiClient.get(`/security/machines/${machineId}/components`, { ...auth(accessToken), params: { status, limit, offset } });
  return res.data;
}

// NVD CPE dictionary candidates for one component. Suggestions only: nothing is stored or scanned until mapComponent.
export async function suggestComponent(accessToken, machineId, { name, vendor }) {
  const res = await apiClient.post(`/security/machines/${machineId}/components/suggest`, { name, vendor: vendor || null }, auth(accessToken));
  return res.data.candidates;
}

// Confirms (source "confirmed") or enters by hand (source "manual") a CPE vendor/product/part; the machine is rescanned.
export async function mapComponent(accessToken, machineId, { name, vendor, cpeVendor, cpeProduct, cpePart, source }) {
  const res = await apiClient.post(`/security/machines/${machineId}/components/map`,
    { name, vendor: vendor || null, cpe_vendor: cpeVendor, cpe_product: cpeProduct, cpe_part: cpePart, source }, auth(accessToken));
  return res.data;
}

export async function unmapComponent(accessToken, machineId, { name, vendor }) {
  const res = await apiClient.delete(`/security/machines/${machineId}/components/map`, { ...auth(accessToken), params: { name, ...(vendor ? { vendor } : {}) } });
  return res.data;
}
